/** Small, dependency-free helpers used by the PDF page window. */

export const PDF_MAX_DEVICE_PIXEL_RATIO = 1.5
export const PDF_PAGE_GAP = 16
export const PDF_PRELOAD_PAGES = 2

export interface PdfPageWindow {
  start: number
  end: number
}

export interface PdfWindowLayout extends PdfPageWindow {
  topOffset: number
  renderedHeight: number
  bottomOffset: number
  totalHeight: number
}

export function shouldMountPdfDocument(fileReady: boolean): boolean {
  // Page count is intentionally not part of this decision: Document itself is
  // responsible for discovering it via onLoadSuccess.
  return fileReady
}

/**
 * Keep the same page pinned when a previously estimated page height becomes
 * exact. Only pages before the visible anchor affect its document offset.
 */
export function getPdfAnchoredScrollTop(
  scrollTop: number,
  changedPageIndex: number,
  anchorPageIndex: number,
  previousHeight: number,
  nextHeight: number,
): number {
  const current = Number.isFinite(scrollTop) ? Math.max(0, scrollTop) : 0
  if (changedPageIndex >= anchorPageIndex) return current
  const delta = nextHeight - previousHeight
  if (!Number.isFinite(delta)) return current
  return Math.max(0, current + delta)
}

/**
 * Keep the number of mounted Page components bounded. The document may contain
 * thousands of pages, but the renderer only needs the viewport and a small
 * look-ahead/look-behind window.
 */
export function getPdfPageWindow(
  pageCount: number,
  centerPage: number,
  preloadPages = PDF_PRELOAD_PAGES,
): PdfPageWindow {
  if (!Number.isFinite(pageCount) || pageCount <= 0) return { start: 0, end: -1 }
  const last = Math.max(0, Math.floor(pageCount) - 1)
  const center = Math.max(0, Math.min(last, Math.floor(centerPage)))
  const radius = Math.max(0, Math.floor(preloadPages))
  return {
    start: Math.max(0, center - radius),
    end: Math.min(last, center + radius),
  }
}

/** Return a safe DPR for canvas rendering. Canvas memory grows with DPR². */
export function getPdfDevicePixelRatio(value: number | undefined): number {
  if (!Number.isFinite(value) || !value || value <= 0) return 1
  return Math.min(PDF_MAX_DEVICE_PIXEL_RATIO, value)
}

/**
 * Find the page containing an offset in a variable-height virtual list.
 * `heights` must contain one entry per page, excluding the gap after the last.
 */
export function findPdfPageAtOffset(offset: number, heights: number[], gap = PDF_PAGE_GAP): number {
  return findPdfPageAtOffsets(offset, getPdfPageOffsets(heights, gap))
}

/** Precompute page starts once; scroll events can then use binary search. */
export function getPdfPageOffsets(heights: number[], gap = PDF_PAGE_GAP): number[] {
  const offsets = new Array<number>(heights.length)
  let cursor = 0
  for (let index = 0; index < heights.length; index++) {
    offsets[index] = cursor
    cursor += Math.max(0, heights[index] ?? 0) + gap
  }
  return offsets
}

/**
 * Calculate the three virtual-list sections from precomputed page starts.
 * The bottom section includes the gap after the last mounted page, so the
 * invariant top + mounted + bottom === total always holds.
 */
export function getPdfWindowLayout(
  heights: number[],
  offsets: number[],
  centerPage: number,
  preloadPages = PDF_PRELOAD_PAGES,
): PdfWindowLayout {
  if (heights.length === 0) {
    return { start: 0, end: -1, topOffset: 0, renderedHeight: 0, bottomOffset: 0, totalHeight: 0 }
  }
  const { start, end } = getPdfPageWindow(heights.length, centerPage, preloadPages)
  const totalHeight = (offsets[heights.length - 1] ?? 0) + Math.max(0, heights[heights.length - 1] ?? 0)
  const topOffset = offsets[start] ?? 0
  const renderedBottom = (offsets[end] ?? topOffset) + Math.max(0, heights[end] ?? 0)
  const renderedHeight = Math.max(0, renderedBottom - topOffset)
  return {
    start,
    end,
    topOffset,
    renderedHeight,
    bottomOffset: Math.max(0, totalHeight - renderedBottom),
    totalHeight,
  }
}

export function findPdfPageAtOffsets(offset: number, offsets: number[]): number {
  if (offsets.length === 0) return 0
  const target = Math.max(0, Number.isFinite(offset) ? offset : 0)
  let low = 0
  let high = offsets.length - 1
  while (low < high) {
    const middle = Math.ceil((low + high) / 2)
    if (offsets[middle]! <= target) low = middle
    else high = middle - 1
  }
  return low
}

export function getPdfPageOffset(index: number, heights: number[], gap = PDF_PAGE_GAP): number {
  const end = Math.max(0, Math.min(Math.floor(index), heights.length))
  let total = 0
  for (let i = 0; i < end; i++) total += Math.max(0, heights[i] ?? 0) + gap
  return total
}
